import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  alertApiRef,
  AnyApiFactory,
  ApiRef,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  analyticsApiRef,
} from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { techRadarApiRef } from '@backstage-community/plugin-tech-radar';
import { OAuth2 } from '@backstage/core-app-api';
import { PrometheusAnalytics } from './analytics/PrometheusAnalytics';
import { InfraTechRadarClient } from './apis/InfraTechRadarClient';

export const oidcAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.oidc',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: oidcAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
      return OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'oidc',
          title: 'Sign In',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
        defaultScopes: ['openid', 'profile', 'email'],
      });
    },
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  createApiFactory({
    api: analyticsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => {
      const baseUrl = configApi.getString('backend.baseUrl');
      return new PrometheusAnalytics(`${baseUrl}/api/stage-deploy/analytics`);
    },
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: techRadarApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
      identityApi: identityApiRef,
    },
    factory: options => new InfraTechRadarClient(options),
  }),
  // ToastApi shim. plugin-notifications >= 0.5.16 (post backstage-cli
  // versions:bump in PR #24) hard-requires `core.toast`, but the legacy
  // app-defaults composition root we use does not register it — the old
  // alertApi is what shipped. Without this factory the SPA crashes during
  // sidebar render with `NotImplementedError: No implementation available
  // for apiRef{core.toast}`, and the entire app never finishes booting.
  // Forward to alertApi so toasts still appear (Material-UI snackbar) and
  // hand back a no-op close() since alertApi has no programmatic dismiss.
  createApiFactory({
    api: toastApiRef,
    deps: { alertApi: alertApiRef },
    factory: ({ alertApi }) => ({
      post: ({ title, description, status }) => {
        const message = description
          ? `${String(title)} — ${String(description)}`
          : String(title);
        // ToastApi has `'neutral'`; AlertApi only knows
        // success|info|warning|error. Map neutral→info and danger→error.
        const severity =
          status === 'danger'
            ? 'error'
            : status === 'neutral' || status === undefined
              ? 'info'
              : status;
        alertApi.post({ message, severity, display: 'transient' });
        return { close: () => {} };
      },
    }),
  }),
];
